import { useRef, useState } from 'react'
import { useLogger } from '../../../components/Logger/hooks/useLogger'
import * as Sentry from '@sentry/react'

export enum RecordingStatus {
  RECORDING = 'recording',
  STOPPED = 'stopped',
  PAUSED = 'paused',
}

export function useNativeMediaRecorder() {
  const logger = useLogger()
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>(RecordingStatus.STOPPED)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const recorderInstance = useRef<MediaRecorder | null>(null)

  const getBestSupportedAudioMimeType = async (): Promise<string> => {
    const preferredTypes = ['audio/webm; codecs=opus', 'audio/ogg; codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg']
    for (const type of preferredTypes) {
      if (window.MediaRecorder && window.MediaRecorder.isTypeSupported(type)) {
        await logger.info(`[useNativeMediaRecorder] ${type} is supported`)
        return type
      }
      await logger.info(`[useNativeMediaRecorder] ${type} is NOT supported`)
    }
    return ''
  }

  const createRecorder = async (stream: MediaStream) => {
    const mimeType = await getBestSupportedAudioMimeType()
    await logger.info(`[useNativeMediaRecorder] Selected MIME type: ${mimeType || 'default (browser chooses)'}`)
    const recorderOptions: MediaRecorderOptions = {}
    if (mimeType) {
      recorderOptions.mimeType = mimeType
    }
    try {
      const recorder = new MediaRecorder(stream, recorderOptions)
      await logger.info(`[useNativeMediaRecorder] MediaRecorder instance created successfully.`)
      recorderInstance.current = recorder
      mediaStreamRef.current = stream
      return recorderInstance.current
    } catch (error: unknown) {
      Sentry.captureException(error)
      await logger.error('[useNativeMediaRecorder] Failed to create MediaRecorder', error)
      recorderInstance.current = null
    }
    return null
  }

  const startRecording = async (timeslice?: number) => {
    if (typeof window === 'undefined' || !window.MediaRecorder) {
      await logger.warn('[useNativeMediaRecorder] MediaRecorder not supported in this environment, returning null')
    }
    try {
      if (recorderInstance.current) {
        recorderInstance.current.start(timeslice)
        await logger.info(`[useNativeMediaRecorder] Recording started${timeslice ? `: with timeslice ${timeslice}ms` : ''}`)
        setRecordingStatus(RecordingStatus.RECORDING)
      }
    } catch (error: unknown) {
      Sentry.captureException(error)
      await logger.error('[useNativeMediaRecorder] Failed to start recording', error)
    }
  }

  const stopRecording = async () => {
    if (recorderInstance.current && (recordingStatus === RecordingStatus.RECORDING || recordingStatus === RecordingStatus.PAUSED)) {
      await logger.info('[useNativeMediaRecorder] Stopping recording')
      recorderInstance.current.stop()
      setRecordingStatus(RecordingStatus.STOPPED)
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getAudioTracks().forEach((track) => {
          track.stop()
          logger.info(`[useNativeMediaRecorder] Stopped audio track: ${track.id}`)
        })
      }
      recorderInstance.current = null
      mediaStreamRef.current = null
    } else {
      await logger.warn('[useNativeMediaRecorder] stopRecording called but not currently recording')
    }
  }

  const pauseRecording = async () => {
    if (recorderInstance.current && recordingStatus === RecordingStatus.RECORDING) {
      await logger.info('[useNativeMediaRecorder] Pausing recording')
      recorderInstance.current.pause()
      setRecordingStatus(RecordingStatus.PAUSED)
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = false
          logger.info(`[useNativeMediaRecorder] Disabled audio track: ${track.id}`)
        })
      }
    } else {
      await logger.warn('[useNativeMediaRecorder] pauseRecording called but not currently recording')
    }
  }

  const resumeRecording = async () => {
    if (recorderInstance.current && recordingStatus === RecordingStatus.PAUSED) {
      await logger.info('[useNativeMediaRecorder] Resuming recording')
      recorderInstance.current.resume()
      setRecordingStatus(RecordingStatus.RECORDING)
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = true
          logger.info(`[useNativeMediaRecorder] Enabled audio track: ${track.id}`)
        })
      }
    } else {
      await logger.warn('[useNativeMediaRecorder] resumeRecording called but not currently paused')
    }
  }

  const requestData = async () => {
    if (recorderInstance.current && recordingStatus === RecordingStatus.RECORDING) {
      await logger.info('[useNativeMediaRecorder] Requesting data from MediaRecorder')
      recorderInstance.current.requestData()
    } else {
      await logger.warn('[useNativeMediaRecorder] requestData called but not currently recording')
    }
  }

  return {
    startRecording,
    createRecorder,
    stopRecording,
    pauseRecording,
    resumeRecording,
    requestData,
    recordingStatus,
    mediaStream: mediaStreamRef.current,
  }
}
